import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { COMPANY, tel, mail } from "@/data/company";
import { useSEO, ORG_JSONLD } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, error, children, required }) => (
  <div>
    <label className="block font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel mb-2">
      {label} {required && <span className="text-amber-brand">*</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400" role="alert">{error}</p>}
  </div>
);

const inputCls =
  "w-full bg-ink border border-line px-4 py-3 text-sm text-steel-light placeholder:text-steel/40 focus:border-amber-brand focus:outline-none";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!/^[+\d][\d\s\-()]{6,24}$/.test(form.phone.trim())) errs.phone = "Please enter a valid phone number.";
    if (form.message.trim().length < 10) errs.message = "Please tell us a little more (min. 10 characters).";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, subject: form.subject || "General enquiry" });
      setSent(true);
      toast.success("Message sent — we'll get back to you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-line bg-ink-2 p-10 text-center" data-testid="contact-success-state">
        <CheckCircle2 size={44} className="text-amber-brand mx-auto mb-5" aria-hidden="true" />
        <h3 className="font-display font-black text-2xl md:text-3xl text-steel-light mb-3">Message received.</h3>
        <p className="text-steel mb-8 max-w-md mx-auto">
          Thank you for writing to Vikas Rolls. Our team will respond to your enquiry as soon as possible.
        </p>
        <Link to="/request-a-quote" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand hover:text-steel-light">
          Need pricing? Request a quote <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="border border-line bg-ink-2 p-7 md:p-9 grid gap-6" data-testid="contact-form">
      <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Your Name" required error={errors.name}>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" data-testid="contact-name-input" />
        </Field>
        <Field label="Company">
          <input className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" data-testid="contact-company-input" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Email" required error={errors.email}>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email-input" />
        </Field>
        <Field label="Phone" required error={errors.phone}>
          <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="contact-phone-input" />
        </Field>
      </div>
      <Field label="Subject">
        <input className={inputCls} value={form.subject} onChange={set("subject")} placeholder="What is this about?" data-testid="contact-subject-input" />
      </Field>
      <Field label="Message" required error={errors.message}>
        <textarea rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your requirement…" data-testid="contact-message-input" />
      </Field>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="group inline-flex items-center justify-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 text-base hover:bg-steel-light disabled:opacity-60"
      >
        {sending ? <Loader2 size={18} className="animate-spin" /> : null}
        {sending ? "Sending…" : "Send Message"}
        {!sending && <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};

export default function Contact() {
  useSEO({
    title: "Contact Us",
    description:
      "Contact Vikas Rolls Private Limited — 1273–1274, MIE, Red Cross Road, Bahadurgarh, Haryana 124507. Call or WhatsApp +91 98104 09131 · info@vikasrolls.in",
    jsonLd: ORG_JSONLD,
  });

  return (
    <>
      <PageHero
        chapter="C"
        label="Contact"
        title="Talk to the People Who Build Your Rolls"
        sub="Call, write or visit — or send your requirement through the form and we'll come back to you."
        crumbs={[{ label: "Contact" }]}
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-start">
          <div className="space-y-8">
            <Reveal>
              <div className="border border-line bg-ink-2 p-7">
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-4 flex items-center gap-2">
                  <MapPin size={14} aria-hidden="true" /> {COMPANY.address.label}
                </p>
                <address className="not-italic text-steel-light text-base leading-relaxed">
                  {COMPANY.address.lines.map((l) => <span key={l} className="block">{l}</span>)}
                </address>
                <div className="h-px bg-line my-5" />
                <div className="grid gap-3 text-sm">
                  <a href={tel(COMPANY.phone.primary)} data-testid="contact-phone-link" className="flex items-center gap-3 text-steel hover:text-amber-brand">
                    <Phone size={15} className="text-amber-brand" aria-hidden="true" /> {COMPANY.phone.primary}
                  </a>
                  <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 text-steel hover:text-amber-brand">
                    <MessageCircle size={15} className="text-amber-brand" aria-hidden="true" /> Chat on WhatsApp
                  </a>
                  <a href={mail(COMPANY.emails.info)} data-testid="contact-email-info" className="flex items-center gap-3 text-steel hover:text-amber-brand">
                    <Mail size={15} className="text-amber-brand" aria-hidden="true" /> {COMPANY.emails.info}
                  </a>
                  <a href={mail(COMPANY.emails.sales)} data-testid="contact-email-sales" className="flex items-center gap-3 text-steel hover:text-amber-brand">
                    <Mail size={15} className="text-amber-brand" aria-hidden="true" /> {COMPANY.emails.sales}
                  </a>
                  <p className="flex items-center gap-3 text-steel">
                    <Clock size={15} className="text-amber-brand" aria-hidden="true" />
                    {COMPANY.businessHoursPlaceholder}
                    <span className="font-mono-tech text-[9px] uppercase tracking-wide text-steel/50">(placeholder — confirm)</span>
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="border border-line overflow-hidden">
                <iframe
                  title="Vikas Rolls Private Limited location — Bahadurgarh, Haryana"
                  src={COMPANY.mapEmbedUrl}
                  className="w-full h-64 grayscale contrast-125 opacity-90"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map-iframe"
                />
                <a
                  href={COMPANY.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-maps-link"
                  className="flex items-center justify-between px-5 py-3.5 border-t border-line bg-ink-2 text-sm text-steel hover:text-amber-brand"
                >
                  <span className="flex items-center gap-3">
                    <MapPin size={15} className="text-amber-brand" aria-hidden="true" />
                    Open Vikas Rolls Private Limited in Google Maps
                  </span>
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.05}>
              <ContactForm />
              <p className="mt-6 text-sm text-steel">
                Need pricing or a technical recommendation?{" "}
                <Link to="/request-a-quote" data-testid="contact-quote-link" className="text-amber-brand hover:text-steel-light underline underline-offset-4">
                  Use the Request-a-Quote form
                </Link>{" "}
                to share full application details and drawings.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
